import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetQuestionDetailsQuery,
  useUpdateQuestionMutation,
} from "../../slices/questionApiSlice";

import { useGetSubjectsQuery } from "../../slices/subjectApiSlice";
import { useGetChaptersQuery } from "../../slices/chapterApiSlice";

const QuestionEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");

  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [explanation, setExplanation] = useState("");

  const [marks, setMarks] = useState(1);
  const [negativeMarks, setNegativeMarks] = useState(0);

  const [difficulty, setDifficulty] = useState("medium");
  const [questionType, setQuestionType] = useState("single");

  const [order, setOrder] = useState(1);

  const [isActive, setIsActive] = useState(true);
  const [isImage, setIsImage] = useState(false);

  const { data: question, isLoading, error } = useGetQuestionDetailsQuery(id);

  const { data: subjects = [] } = useGetSubjectsQuery();

  const { data: chapters = [] } = useGetChaptersQuery(subject, {
    skip: !subject,
  });

  const [updateQuestion, { isLoading: loadingUpdate }] =
    useUpdateQuestionMutation();

  useEffect(() => {
    if (question) {
      setSubject(question.subject?._id || question.subject || "");

      setChapter(question.chapter?._id || question.chapter || "");

      setQuestionText(question.questionText || "");

      setOptions(
        question.options?.length ? question.options : ["", "", "", ""],
      );

      setCorrectAnswer(question.correctAnswer || "");

      setExplanation(question.explanation || "");

      setMarks(question.marks ?? 1);

      setNegativeMarks(question.negativeMarks ?? 0);

      setDifficulty(question.difficulty || "medium");

      setQuestionType(question.questionType || "single");

      setOrder(question.order ?? 1);

      setIsActive(question.isActive ?? true);

      setIsImage(question.isImage ?? false);
    }
  }, [question]);

  const optionChangeHandler = (index, value) => {
    const updated = [...options];

    updated[index] = value;

    setOptions(updated);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject || !chapter || !questionText || !correctAnswer) {
      toast.error("Subject, chapter, question and correct answer are required");

      return;
    }

    if (options.some((item) => !item.trim())) {
      toast.error("All options are required");

      return;
    }

    try {
      await updateQuestion({
        questionId: id,

        subject,

        chapter,

        questionText,

        options,

        correctAnswer,

        explanation,

        marks: Number(marks),

        negativeMarks: Number(negativeMarks),

        difficulty,

        questionType,

        order: Number(order),

        isImage,

        isActive,
      }).unwrap();

      toast.success("Question updated successfully");

      navigate(`/questions/${id}`);
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Failed to update question",
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading question...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 text-red-700 p-5 rounded-xl">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to={`/questions/${id}`}
          className="inline-flex mb-6 bg-white px-5 py-3 rounded-xl shadow text-blue-600"
        >
          ← Back to Question
        </Link>

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-6 md:p-10">
          <h1 className="text-4xl font-bold text-center mb-8">Edit Question</h1>

          {loadingUpdate && (
            <div className="bg-blue-100 text-blue-700 p-4 rounded-xl mb-5 text-center">
              Updating question...
            </div>
          )}

          <form onSubmit={submitHandler} className="grid md:grid-cols-2 gap-6">
            {/* Subject */}

            <div>
              <label className="font-semibold">Subject *</label>

              <select
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setChapter("");
                }}
                className="w-full border rounded-xl p-3"
              >
                <option value="">Select Subject</option>

                {subjects.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter */}

            <div>
              <label className="font-semibold">Chapter *</label>

              <select
                value={chapter}
                onChange={(e) => setChapter(e.target.value)}
                className="w-full border rounded-xl p-3"
              >
                <option value="">Select Chapter</option>

                {chapters.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Question Text */}

            <div className="md:col-span-2">
              <label className="font-semibold">Question *</label>

              <textarea
                rows="4"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="w-full border rounded-xl p-3"
              />
            </div>

            {/* Options */}

            <div className="md:col-span-2">
              <label className="font-semibold">Options *</label>

              {options.map((option, index) => (
                <div key={index} className="flex gap-3 mt-3">
                  <span>{String.fromCharCode(65 + index)}.</span>

                  <input
                    value={option}
                    onChange={(e) => optionChangeHandler(index, e.target.value)}
                    className="flex-1 border rounded-xl p-3"
                  />
                </div>
              ))}
            </div>

            {/* Correct Answer */}

            <input
              value={correctAnswer}
              onChange={(e) => setCorrectAnswer(e.target.value)}
              placeholder="Correct answer"
              className="border rounded-xl p-3"
            />

            {/* Question Type */}

            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
              className="border rounded-xl p-3"
            >
              <option value="single">Single Choice</option>

              <option value="multiple">Multiple Choice</option>
            </select>

            <input
              type="number"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              className="border rounded-xl p-3"
              placeholder="Marks"
            />

            <input
              type="number"
              value={negativeMarks}
              onChange={(e) => setNegativeMarks(e.target.value)}
              className="border rounded-xl p-3"
              placeholder="Negative Marks"
            />

            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="border rounded-xl p-3"
            >
              <option value="easy">Easy</option>

              <option value="medium">Medium</option>

              <option value="hard">Hard</option>
            </select>

            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              className="border rounded-xl p-3"
              placeholder="Order"
            />

            <textarea
              rows="4"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Explanation"
              className="md:col-span-2 border rounded-xl p-3"
            />

            {/* Image Question */}

            <label className="flex gap-3 items-center">
              <input
                type="checkbox"
                checked={isImage}
                onChange={(e) => setIsImage(e.target.checked)}
              />
              Question contains image
            </label>

            {/* Status */}

            <label className="flex gap-3 items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>

            <button
              disabled={loadingUpdate}
              className="md:col-span-2 bg-blue-600 text-white rounded-xl py-4 font-semibold"
            >
              {loadingUpdate ? "Updating..." : "Update Question"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditScreen;
